import React, { useEffect, useRef } from 'react';

interface AnimatedBackgroundProps {
  density?: number;
  speed?: number;
}

export const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({ 
  density = 15, 
  speed = 1.5 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Create shapes
    const shapes: Shape[] = [];
    
    for (let i = 0; i < density; i++) {
      shapes.push(new Shape(canvas, ctx, speed));
    }
    
    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      shapes.forEach(shape => {
        shape.update();
        shape.draw();
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [density, speed]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="fixed top-0 left-0 w-full h-full pointer-events-none opacity-40"
    />
  );
};

// Shape class
class Shape {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private x: number;
  private y: number;
  private size: number;
  private angle: number = 0;
  private rotationSpeed: number;
  private velocityX: number;
  private velocityY: number;
  private opacity: number;
  private type: 'cube' | 'triangle' | 'circle';
  private color: string;
  private speedFactor: number;
  
  constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, speedFactor: number = 1) {
    this.canvas = canvas;
    this.ctx = ctx;
    this.speedFactor = speedFactor;
    this.size = Math.random() * 35 + 15;
    this.x = Math.random() * (canvas.width - this.size * 2) + this.size;
    this.y = Math.random() * (canvas.height - this.size * 2) + this.size;
    this.rotationSpeed = (Math.random() - 0.5) * 0.02 * this.speedFactor;
    this.velocityX = (Math.random() - 0.5) * 1 * this.speedFactor;
    this.velocityY = (Math.random() - 0.5) * 1 * this.speedFactor;
    this.opacity = Math.random() * 0.6 + 0.3;
    
    const types = ['cube', 'triangle', 'circle'];
    this.type = types[Math.floor(Math.random() * types.length)] as 'cube' | 'triangle' | 'circle';
    
    // Using only yellow for Nike branding
    this.color = '#f3e252';
  }
  
  draw() {
    this.ctx.save();
    this.ctx.globalAlpha = this.opacity;
    this.ctx.translate(this.x, this.y);
    this.ctx.rotate(this.angle);
    
    this.ctx.fillStyle = this.color;
    this.ctx.strokeStyle = this.color;
    this.ctx.lineWidth = 2;
    
    switch (this.type) {
      case 'cube':
        this.drawCube();
        break;
      case 'triangle':
        this.drawTriangle();
        break;
      case 'circle':
        this.drawCircle();
        break;
    }
    
    this.ctx.restore();
  }
  
  update() {
    this.angle += this.rotationSpeed;
    this.x += this.velocityX;
    this.y += this.velocityY;
    
    // Bounce off edges
    if (this.x <= this.size || this.x >= this.canvas.width - this.size) {
      this.velocityX *= -1;
    }
    
    if (this.y <= this.size || this.y >= this.canvas.height - this.size) {
      this.velocityY *= -1;
    }
  }
  
  private drawCube() {
    const size = this.size;
    const perspective = size * 0.5;
    
    // Front face
    this.ctx.beginPath();
    this.ctx.rect(-size/2, -size/2, size, size);
    this.ctx.fill();
    
    // Top face (pseudo-3D)
    this.ctx.beginPath();
    this.ctx.moveTo(-size/2, -size/2);
    this.ctx.lineTo(-size/2 + perspective, -size/2 - perspective);
    this.ctx.lineTo(size/2 + perspective, -size/2 - perspective);
    this.ctx.lineTo(size/2, -size/2);
    this.ctx.closePath();
    this.ctx.fillStyle = this.adjustColor(this.color, 20);
    this.ctx.fill();
    
    // Side face (pseudo-3D)
    this.ctx.beginPath();
    this.ctx.moveTo(size/2, -size/2);
    this.ctx.lineTo(size/2 + perspective, -size/2 - perspective);
    this.ctx.lineTo(size/2 + perspective, size/2 - perspective);
    this.ctx.lineTo(size/2, size/2);
    this.ctx.closePath();
    this.ctx.fillStyle = this.adjustColor(this.color, -20);
    this.ctx.fill();
  }
  
  private drawTriangle() {
    const size = this.size;
    
    this.ctx.beginPath();
    this.ctx.moveTo(0, -size);
    this.ctx.lineTo(-size, size);
    this.ctx.lineTo(size, size);
    this.ctx.closePath();
    this.ctx.fill();
  }
  
  private drawCircle() {
    this.ctx.beginPath();
    this.ctx.arc(0, 0, this.size, 0, Math.PI * 2);
    this.ctx.fill();
  }
  
  private adjustColor(color: string, amount: number): string {
    const colorValue = parseInt(color.slice(1), 16);
    const r = Math.max(0, Math.min(255, ((colorValue >> 16) & 0xff) + amount));
    const g = Math.max(0, Math.min(255, ((colorValue >> 8) & 0xff) + amount));
    const b = Math.max(0, Math.min(255, (colorValue & 0xff) + amount));
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  }
}
